import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Calendar, FileText, Search, Users, Mail, MapPin, Building } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const engagementOptions = [
  {
    icon: Calendar,
    title: "Monthly Support",
    description: "Linux/HPC/DevOps support with SLAs."
  },
  {
    icon: FileText,
    title: "Project Delivery",
    description: "AI systems, modernization, platform builds."
  },
  {
    icon: Search,
    title: "Audit & Roadmap",
    description: "Architecture review + upgrade plan."
  },
  {
    icon: Users,
    title: "Training",
    description: "Runbooks, ops readiness, team enablement."
  }
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-[#2997FF] text-sm font-medium tracking-widest uppercase mb-4">Get in Touch</p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold text-[#F5F5F7] tracking-tight mb-6" data-testid="contact-title">
              Contact
            </h1>
            <p className="text-lg md:text-xl text-[#86868B] max-w-3xl mx-auto">
              Share a short note and we'll respond with the best next step (call / proposal / scope checklist).
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left - Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <form onSubmit={handleSubmit} className="glass rounded-3xl p-8" data-testid="contact-form">
                <h2 className="text-2xl font-semibold text-[#F5F5F7] mb-6">Send us a message</h2>
                <div className="space-y-6">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F7] mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="w-full form-input px-4 py-3"
                      required
                      data-testid="contact-name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F7] mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      className="w-full form-input px-4 py-3"
                      required
                      data-testid="contact-email"
                    />
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F7] mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your company name"
                      className="w-full form-input px-4 py-3"
                      data-testid="contact-company"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F7] mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us what you need..."
                      rows={5}
                      className="w-full form-input px-4 py-3 resize-none"
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary px-8 py-4 rounded-full text-base font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Get in Touch
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>

            {/* Right - Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              {/* Contact Info */}
              <div className="service-card p-8">
                <h3 className="text-xl font-semibold text-[#F5F5F7] mb-6">Company Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <Building size={20} className="text-[#2997FF] mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-[#F5F5F7] font-medium">Appface Technologies Pvt Ltd</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin size={20} className="text-[#2997FF] mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-[#86868B]">Bengaluru, India</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Mail size={20} className="text-[#2997FF] mt-1 flex-shrink-0" />
                    <div>
                      <a href="mailto:ceo@appface.in" className="text-[#2997FF] hover:underline">
                        ceo@appface.in
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Engagement Options */}
              <div>
                <h3 className="text-xl font-semibold text-[#F5F5F7] mb-6">Engagement options</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {engagementOptions.map((option, index) => {
                    const Icon = option.icon;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                        className="engagement-card"
                        data-testid={`engagement-${index}`}
                      >
                        <Icon size={24} className="text-[#2997FF] mb-3" />
                        <h4 className="text-base font-semibold text-[#F5F5F7] mb-1">{option.title}</h4>
                        <p className="text-sm text-[#86868B]">{option.description}</p>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
